import { useIntl } from '@/intl/useIntl'
import { Item } from '@/scopes/common/components/Drawer/Item'
import { useQuickSearchStore } from '@/scopes/search/components/QuickSearchBar/store'
import { MenuItem } from '@sunwebgroup/uds-ui/menu'
import { Popover } from '@sunwebgroup/ui-components/uds'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import styles from './DestinationDropdown.module.css'

interface DestinationDropdownProps {
  id: string
  options: { name: string, url: string, isSelected: boolean }[]
  placeholder?: string
  label: string
  disabled?: boolean
  handleChange: (value?: string) => void
}

export default function DestinationDropdown({
  id,
  options,
  placeholder,
  label,
  disabled,
  handleChange,
}: DestinationDropdownProps) {
  const intl = useIntl()
  const [open, setOpen] = useState(false)
  const { activeItemId, setActiveItem } = useQuickSearchStore()
  const selectedOption = useMemo(() => options.find(option => option.isSelected), [options])
  const selectedValue = selectedOption?.url
  const formattedText = selectedOption?.name
  const isActive = activeItemId === id
  const hasActiveSibling = activeItemId !== null && !isActive
  const selectedRef = useRef<HTMLButtonElement | null>(null)

  const handleOpenToggle = (open: boolean) => {
    setOpen(open)
    if (open) {
      // Timeout is used as a workaround for focus/blur issues on iOS and Radix UI.
      // Without this delay, opening a new filter can immediately close it due to focus management quirks.
      setTimeout(() => setActiveItem(id), 0)
    }
    else {
      setActiveItem(null)
    }
  }

  useEffect(() => {
    if (open && selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: 'center' })
      selectedRef.current.focus()
    }
  }, [open, selectedRef.current])

  return (
    <Popover.Root open={open} defaultOpen={false} onOpenChange={handleOpenToggle}>
      <Popover.Trigger>
        <Item
          disabled={disabled}
          placeholder={placeholder}
          title={label}
          isActive={isActive}
          hasActiveSibling={hasActiveSibling}
          selectionFormattedText={formattedText}
          data-qa={`${id}-dropdown`}
          biggerBreakpoint
        />
      </Popover.Trigger>
      <Popover.Content autoFocus position="bottom" className={styles.menuContent} title={label} closeText={intl.formatMessage({ id: 'close' })}>
        {options.map(({ name, url }) => (
          <MenuItem
            glyph={url === selectedValue ? 'CheckMark' : undefined}
            key={`${name}-${url}`}
            id={url}
            value={url}
            onClick={() => handleChange(url)}
            ref={url === selectedValue ? selectedRef : undefined}
            data-qa={`${id}-option`}
          >
            {name}
          </MenuItem>
        ))}
      </Popover.Content>
    </Popover.Root>
  )
}
